import React from 'react';
import { SectionLabel } from '@/components/ui/section-label';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PackageSearch, Plus, Save } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { Spinner } from '@/components/ui/spinner';
import { EmptyState } from '@/components/shared/EmptyState';
import { SimpleSelect } from '@/components/form/inputs/SimpleSelect';
import { useTranslation } from '@/hooks/useTranslation';
import { useArticleManager } from '../hooks/useArticleManager';
import { ArchiveAction } from './ArchiveAction';
import type { TraceabilityApiClient } from '../api/TraceabilityApiClient';

type ArticleManagerPanelProps = {
  companyName: string;
  traceabilityApiClient: TraceabilityApiClient | null;
};

export const ArticleManagerPanel: React.FC<ArticleManagerPanelProps> = ({ companyName, traceabilityApiClient }) => {
  const { t } = useTranslation();
  const {
    formArticle,
    setFormArticle,
    selectedId,
    formError,
    isLoading,
    isSaving,
    isArchiving,
    archiveConfirm,
    setArchiveConfirm,
    showArchived,
    setShowArchived,
    selectedArticle,
    isEditing,
    visibleArticles,
    activeArticles,
    handleAddOrUpdate,
    handleArchive,
    handleRestore,
    handleSelectArticle,
    handleNewArticle,
  } = useArticleManager(companyName, traceabilityApiClient);

  return (
    <div className="flex flex-col gap-4">
      {formError && <Callout tone="error">{formError}</Callout>}

      {/* Mobile article selector */}
      <div className="mb-4 md:hidden">
        <SimpleSelect
          aria-label={t('traceability.articleManager.list.title')}
          value={selectedId !== undefined ? String(selectedId) : null}
          onChange={(next) => {
            const id = next ? Number(next) : undefined;
            const a = id !== undefined ? activeArticles.find((a) => a.id === id) : undefined;
            if (a) handleSelectArticle(a);
            else handleNewArticle();
          }}
          disabled={isLoading || isSaving || isArchiving}
          noneLabel={t('traceability.articleManager.form.new')}
          options={activeArticles.map((a) => ({
            value: a.id !== undefined ? String(a.id) : a.name,
            label: a.name,
          }))}
        />
      </div>

      <div className="grid gap-6 md:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        {/* Form panel */}
        <Card>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <SectionLabel icon={PackageSearch}>
              {t('traceability.articleManager.form.sectionTitle')}
            </SectionLabel>
            {selectedId !== undefined && formArticle.name && (
              <Badge tone="primary" caps>
                {formArticle.name}
              </Badge>
            )}
          </div>
          <SimpleInput
            name="article-name"
            label={t('traceability.articleManager.form.fields.name.label')}
            value={formArticle.name}
            onChange={(val) => setFormArticle((prev) => ({ ...prev, name: val as string }))}
            placeholder={t('traceability.articleManager.form.fields.name.placeholder')}
            required
          />
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button
              className="flex-1"
              onClick={handleAddOrUpdate}
              loading={isSaving}
              disabled={isLoading}
            >
              {isSaving ? (
                t('traceability.articleManager.form.submit.saving')
              ) : (
                <>
                  <Save aria-hidden />
                  {isEditing
                    ? t('traceability.articleManager.form.submit.update')
                    : t('traceability.articleManager.form.submit.save')}
                </>
              )}
            </Button>
            <Button
              className="flex-1"
              variant="outline"
              onClick={handleNewArticle}
              disabled={isSaving || isLoading}
            >
              <Plus aria-hidden />
              {t('traceability.articleManager.form.new')}
            </Button>
          </div>
        </Card>

        {/* List panel — desktop only */}
        <Card padding="sm" className="hidden md:flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-semibold text-foreground">
              {t('traceability.articleManager.list.title')}
            </p>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-border accent-primary"
                />
                {t('traceability.articleManager.list.showArchived')}
              </label>
              <span className="text-xs text-muted-foreground">
                {t('traceability.articleManager.list.count', { count: activeArticles.length })}
              </span>
            </div>
          </div>

          <div className="max-h-72 space-y-2 overflow-auto pr-1">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Spinner size="lg" className="text-primary" />
              </div>
            )}
            {!isLoading && visibleArticles.length === 0 && (
              <EmptyState size="compact" title={t('traceability.articleManager.list.empty')} />
            )}
            {!isLoading &&
              visibleArticles.map((article) => (
                <button
                  key={article.id ?? article.name}
                  type="button"
                  onClick={() => handleSelectArticle(article)}
                  disabled={isSaving || isArchiving}
                  className={cn(
                    'w-full rounded-xl border px-4 py-3 text-left shadow-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50',
                    article.archived
                      ? 'opacity-50 border-border bg-card'
                      : cn(
                          'transition hover:-translate-y-0.5 hover:shadow-md',
                          article.id === selectedId
                            ? 'border-primary/60 bg-primary/10 text-primary'
                            : 'border-border bg-card hover:border-primary/40 hover:bg-primary/5'
                        )
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold">{article.name}</p>
                    {article.archived && (
                      <Badge size="sm" caps>
                        {t('traceability.articleManager.list.archivedBadge')}
                      </Badge>
                    )}
                  </div>
                </button>
              ))}
          </div>

          <ArchiveAction
            selected={selectedArticle}
            i18nPrefix="traceability.articleManager"
            archiveConfirm={archiveConfirm}
            isArchiving={isArchiving}
            isSaving={isSaving}
            onArchiveClick={() => setArchiveConfirm(true)}
            onArchiveConfirm={handleArchive}
            onArchiveCancel={() => setArchiveConfirm(false)}
            onRestore={handleRestore}
          />
        </Card>
      </div>
    </div>
  );
};
